import React, { FC, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Heading,
  Icon,
  Modal,
  Text,
  TextButton,
} from '@amzn/storm-ui';
import Spinner from '@components/Spinner';
import CombineAccountsLayout from '@features/combine-accounts/components/CombineAccountsLayout';
import CombineAccountsSteps, {
  CombineAccountsStep,
} from '@features/combine-accounts/components/CombineAccountsSteps';
import CombineAccountsActionButtons from '@features/combine-accounts/components/CombineAccountsActionButtons';
import { COMBINE_ACCOUNTS_LANDING_PATH } from 'src/shared/routes';
// Shared with the choose page's modal so both loading states look identical, and so the height
// reasoning written there is not duplicated and left to drift.
import { ModalLoadingBody } from '../combineChoose/style';
import { skipToken } from '@reduxjs/toolkit/query/react';
import {
  useGetReviewAccountsQuery,
  useCreateAccountCombinationMutation,
} from 'src/shared/services/combine-accounts/combineAccounts';
import useCombineProgress from './useCombineProgress';
import type { CombineNavigationState } from '../combineLanding/components/CombineProgressNotifier';
import useIntl from '@i18n/useIntl';
import { addNotification } from 'src/shared/state/notifications-slice';
import { SnackbarTypes } from '@components/notifications/enums';
import {
  COMBINE_ACCOUNTS_REVIEW_HEADING_KEY,
  COMBINE_ACCOUNTS_REVIEW_ACCOUNTS_TO_COMBINE_KEY,
  COMBINE_ACCOUNTS_REVIEW_LINKED_TO_KEY,
  COMBINE_ACCOUNTS_REVIEW_NEW_ACCOUNT_KEY,
  COMBINE_ACCOUNTS_DETAILS_LOADING_KEY,
  COMBINE_ACCOUNTS_DETAILS_ERROR_KEY,
  COMBINE_ACCOUNTS_DETAILS_MISSING_PARAMS_KEY,
  COMBINE_ACCOUNTS_CONFIRM_HEADER_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BODY_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_CAMPAIGNS_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_USERS_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_FEATURES_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_MANAGE_KEY,
  COMBINE_ACCOUNTS_CONFIRM_CHECKBOX_KEY,
  COMBINE_ACCOUNTS_CANCEL_KEY,
  COMBINE_ACCOUNTS_CONFIRM_SUBMIT_KEY,
  COMBINE_ACCOUNTS_CONFIRM_ERROR_KEY,
  COMBINE_ACCOUNTS_CONFIRM_LOADING_KEY,
  COMBINE_ACCOUNTS_REVIEW_TYPE_DSP_KEY,
  COMBINE_ACCOUNTS_REVIEW_TYPE_SA_KEY,
  COMBINE_ACCOUNTS_REVIEW_TYPE_ADVERTISER_KEY,
  COMBINE_ACCOUNTS_CONFIRM_BULLET_INVOICES_KEY,
  GA_PARAM,
  CFID_PARAM,
} from '@features/combine-accounts/constants';

import AmazonAdsAgreementLink from '@features/combine-accounts/components/AmazonAdsAgreementLink';

const COMBINE_REVIEW_PAGE_ID = 'combine-accounts-review-page';

const boldStyle = { fontWeight: 700 } as const;

/**
 * One account block on the review page: name, type, id, and the manager account it links to.
 *
 * Shared by all three blocks so the two accounts being combined and the account they produce cannot
 * drift apart in layout. Copy arrives resolved from the caller and `linkedToLabel` is required, so
 * the linked-to branch cannot render untranslated.
 */
interface ReviewAccountProps {
  name: string;
  typeLabel: string;
  accountId: string;
  linkedToLabel: string;
  managerAccountName?: string | null;
  managerAccountId?: string | null;
}

const ReviewAccount: FC<ReviewAccountProps> = ({
  name,
  typeLabel,
  accountId,
  linkedToLabel,
  managerAccountName,
  managerAccountId,
}) => (
  <Box paddingBlockEnd="xxsmall" paddingInlineStart="xxsmall">
    <Text style={boldStyle}>{name}</Text>
    {/*
      Type and id each sit on their own line beneath the name, per the approved
      mock. They were previously joined to the name as "{name} - {type}", which
      also left the id as the only detail on its own row.
    */}
    <Text renderAs="span" style={{ display: 'block' }}>
      {typeLabel}
    </Text>
    <Text renderAs="span" style={{ display: 'block' }}>
      {accountId}
    </Text>
    {managerAccountName && (
      <Flex>
        <Text color="secondary" style={{ marginRight: '4px' }}>
          {linkedToLabel}
        </Text>
        {/*
          The manager account id sits beneath its name rather than beside the
          label, so the two lines align with each other.
        */}
        <Flex direction="column">
          <Text>{managerAccountName}</Text>
          {managerAccountId && <Text>{managerAccountId}</Text>}
        </Flex>
      </Flex>
    )}
  </Box>
);

const CombineAccountsReviewPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { intl } = useIntl();
  const [searchParams] = useSearchParams();
  const globalAccountId = searchParams.get(GA_PARAM);
  const cfid = searchParams.get(CFID_PARAM);
  const selectedAccountId = searchParams.get('selectedAccountId');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const {
    data: reviewData,
    isLoading,
    isError,
  } = useGetReviewAccountsQuery(
    globalAccountId && cfid ? { globalAccountId, cfid } : skipToken,
  );

  const [createCombination, { isLoading: isSubmitting }] =
    useCreateAccountCombinationMutation();
  const { isWaiting, waitForCombine } = useCombineProgress();
  // Lets the modal's cancel button stop a watch already in flight.
  const watchRef = useRef<AbortController>();
  /*
    Blocks a second confirm synchronously, before the first await. The disabled prop below cannot
    do this on its own: it is driven by React state, which updates a render later, so two clicks
    landing in the same tick both get through and submit two combines against the same pair of
    accounts. A ref is set during the click itself, so the second one sees it immediately.
  */
  const submitLockRef = useRef(false);
  // Submitting and watching are one state to the user: the combine is happening.
  const isBusy = isSubmitting || isWaiting;
  // Stops a watch when the user leaves the page mid-combine. Cancel cannot cover this: it is
  // disabled while the work is in flight, and a browser-back does not go through it.
  useEffect(() => () => watchRef.current?.abort(), []);

  const handleCancel = () => navigate(COMBINE_ACCOUNTS_LANDING_PATH);
  const handleGoBack = () => navigate(-1);
  const handleSubmit = () => setShowConfirmation(true);
  const handleClose = () => navigate(COMBINE_ACCOUNTS_LANDING_PATH);

  const handleConfirmCombine = async () => {
    /*
      Both ids are required, matching the render guard that decides whether this page shows at all.
      The account id is what the combine targets and what the wait polls, so proceeding without it
      would submit a combine with no target and then skip the wait, which looks from the outside
      exactly like the stale-redirect bug this exists to fix.
    */
    if (!cfid || !globalAccountId) return;

    // Checked and set before anything awaits, so a second click in the same tick is dropped
    // rather than creating a duplicate combine.
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    const entityId = reviewData?.newAccount?.entityId;
    const redirectToAccount = () =>
      window.location.assign(
        entityId
          ? `/campaign-manager/all-campaigns?entityId=${entityId}`
          : '/ads-account/combine',
      );

    try {
      await createCombination({
        dspAccountId: cfid,
        sponsoredAdsAccountId: globalAccountId,
        selectedAccountId: selectedAccountId ?? undefined,
      }).unwrap();

      /*
        The combine has only been ACCEPTED at this point. The work that makes the account look
        combined runs asynchronously for tens of seconds afterwards, so redirecting here is what
        lands the user on a console still describing their old account.
      */
      const controller = new AbortController();
      watchRef.current = controller;
      const progress = await waitForCombine(globalAccountId, {
        signal: controller.signal,
      });

      // The user closed the modal while we were waiting. The combine carries on server-side; we
      // just stop reporting on it, rather than moving them somewhere they did not ask to go.
      if (controller.signal.aborted) {
        return;
      }

      if (progress.outcome === 'failed') {
        setShowConfirmation(false);
        navigate(COMBINE_ACCOUNTS_LANDING_PATH);
        dispatch(
          addNotification({
            id: 'combine-accounts-error',
            type: SnackbarTypes.Error,
            message: intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_ERROR_KEY),
          }),
        );
        return;
      }

      /*
        Still running when the modal's window closed. Rather than redirect into an account that is
        still being rewritten, hand the combine to the landing page, which reports it and redirects
        once it finishes. The entity id travels too: the notifier needs it to build the campaign
        manager link, and only this page has it.
      */
      if (progress.outcome === 'pending') {
        setShowConfirmation(false);
        const handover: CombineNavigationState = {
          combiningAccountId: globalAccountId,
          entityId,
        };
        navigate(COMBINE_ACCOUNTS_LANDING_PATH, { state: handover });
        return;
      }

      setShowConfirmation(false);
      redirectToAccount();
    } catch (error) {
      setShowConfirmation(false);
      navigate(COMBINE_ACCOUNTS_LANDING_PATH);
      dispatch(
        addNotification({
          id: 'combine-accounts-error',
          type: SnackbarTypes.Error,
          message: intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_ERROR_KEY),
        }),
      );
    } finally {
      submitLockRef.current = false;
    }
  };

  const handleCloseConfirmation = () => {
    watchRef.current?.abort();
    setShowConfirmation(false);
    setConfirmChecked(false);
  };

  const actionButtons = (
    <CombineAccountsActionButtons
      onCancel={handleCancel}
      onGoBack={handleGoBack}
      onSubmit={handleSubmit}
      submitDisabled={isLoading || isError || !reviewData || isSubmitting}
    />
  );

  if (!globalAccountId || !cfid) {
    return (
      <Box id={COMBINE_REVIEW_PAGE_ID} data-testid={COMBINE_REVIEW_PAGE_ID}>
        <CombineAccountsLayout
          actionButtons={actionButtons}
          onClose={handleClose}
        >
          <Text>
            {intl.getMessage(COMBINE_ACCOUNTS_DETAILS_MISSING_PARAMS_KEY)}
          </Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box id={COMBINE_REVIEW_PAGE_ID} data-testid={COMBINE_REVIEW_PAGE_ID}>
        <CombineAccountsLayout
          actionButtons={actionButtons}
          onClose={handleClose}
        >
          <Flex justifyContent="center" paddingBlockStart="large">
            <Spinner
              text={intl.getMessage(COMBINE_ACCOUNTS_DETAILS_LOADING_KEY)}
              isFullScreen={false}
            />
          </Flex>
        </CombineAccountsLayout>
      </Box>
    );
  }

  if (isError || !reviewData) {
    return (
      <Box id={COMBINE_REVIEW_PAGE_ID} data-testid={COMBINE_REVIEW_PAGE_ID}>
        <CombineAccountsLayout
          actionButtons={actionButtons}
          onClose={handleClose}
        >
          <Text>{intl.getMessage(COMBINE_ACCOUNTS_DETAILS_ERROR_KEY)}</Text>
        </CombineAccountsLayout>
      </Box>
    );
  }

  const { dspAccount, sponsoredAdsAccount, newAccount } = reviewData;

  // Determine the new account display name based on selectedAccountId
  const linkedToLabel = intl.getMessage(COMBINE_ACCOUNTS_REVIEW_LINKED_TO_KEY);

  const newAccountDisplayName =
    selectedAccountId === cfid ? dspAccount.name : newAccount.name;

  return (
    <Box id={COMBINE_REVIEW_PAGE_ID} data-testid={COMBINE_REVIEW_PAGE_ID}>
      <CombineAccountsLayout
        actionButtons={actionButtons}
        onClose={handleClose}
      >
        <Box paddingBlockEnd="small">
          <CombineAccountsSteps current={CombineAccountsStep.Review} />
        </Box>

        {/*
          Card carries its own padding (block-start 2rem, inline 2rem) which the
          `padding` prop does not clear, so it is zeroed inline where the
          specificity actually wins. Insets then live on the Boxes below, which is
          what lets the page title and both section headings share one edge
          instead of being inset by two different components.
        */}
        <Card style={{ padding: 0 }}>
          <Box
            paddingInlineStart="small"
            paddingInlineEnd="small"
            paddingBlockStart="small"
            paddingBlockEnd="small"
          >
            <Heading renderAs="h4">
              {intl.getMessage(COMBINE_ACCOUNTS_REVIEW_HEADING_KEY)}
            </Heading>
          </Box>

          {/* Full-bleed, as the mock shows it spanning the whole card. */}
          <Divider />

          <Box
            paddingInlineStart="small"
            paddingInlineEnd="small"
            paddingBlockStart="xsmall"
            paddingBlockEnd="small"
          >
            <Box
              paddingBlockEnd="xxxsmall"
              /*
                Storm gives this Box a block-start and inline padding of 2rem that
                was never asked for, which indented both section headings past the
                page title. Zeroed inline, where specificity wins over the
                generated class.
              */
              style={{ paddingInline: 0, paddingBlockStart: 0 }}
            >
              <Heading renderAs="h6" style={{ marginBottom: '4px' }}>
                {intl.getMessage(
                  COMBINE_ACCOUNTS_REVIEW_ACCOUNTS_TO_COMBINE_KEY,
                )}
              </Heading>

              {/*
                Sponsored Ads first, then Amazon DSP, matching the approved mock.
                No linked-to is rendered for the Sponsored Ads account because the
                review response carries manager-account fields only on the DSP
                account.
              */}
              <ReviewAccount
                name={sponsoredAdsAccount.name}
                typeLabel={intl.getMessage(COMBINE_ACCOUNTS_REVIEW_TYPE_SA_KEY)}
                accountId={sponsoredAdsAccount.globalAccountId}
                linkedToLabel={linkedToLabel}
              />

              <ReviewAccount
                name={dspAccount.name}
                typeLabel={intl.getMessage(
                  COMBINE_ACCOUNTS_REVIEW_TYPE_DSP_KEY,
                )}
                accountId={dspAccount.cfid}
                linkedToLabel={linkedToLabel}
                managerAccountName={dspAccount.managerAccountName}
                managerAccountId={dspAccount.managerAccountId}
              />
            </Box>

            <Divider marginBlockStart="xsmall" marginBlockEnd="xsmall" />

            <Box
              paddingBlockEnd="xxxsmall"
              /* See the note on the section above: Storm padding zeroed. */
              style={{ paddingInline: 0, paddingBlockStart: 0 }}
            >
              <Heading renderAs="h6" style={{ marginBottom: '4px' }}>
                {intl.getMessage(COMBINE_ACCOUNTS_REVIEW_NEW_ACCOUNT_KEY)}
              </Heading>
              {/*
                The combined account inherits the DSP account's manager account:
                the review response carries manager-account fields only there, and
                the combination links the produced account to the same one.
              */}
              <ReviewAccount
                name={newAccountDisplayName}
                typeLabel={intl.getMessage(
                  COMBINE_ACCOUNTS_REVIEW_TYPE_ADVERTISER_KEY,
                )}
                accountId={newAccount.globalAccountId}
                linkedToLabel={linkedToLabel}
                managerAccountName={dspAccount.managerAccountName}
                managerAccountId={dspAccount.managerAccountId}
              />
            </Box>
          </Box>
        </Card>
      </CombineAccountsLayout>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmation}
        header={intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_HEADER_KEY)}
        /*
          Dismissal is blocked while the combine is in flight, matching the choose page's modal. The
          operation has already started server-side and cannot be undone, so a control that looks
          like it cancels would be lying. Both halves are needed: disabling the close button makes
          the state visible rather than leaving something that silently does nothing, and
          neutralising onClose covers the routes that skip that button, such as Escape.

          Bounded at ten seconds, after which this page hands off and navigates away by itself.
        */
        closeButtonProps={{ disabled: isBusy }}
        onClose={isBusy ? () => undefined : handleCloseConfirmation}
        footer={
          <Flex justifyContent="flex-end" gap="small">
            <TextButton disabled={isBusy} onClick={handleCloseConfirmation}>
              {intl.getMessage(COMBINE_ACCOUNTS_CANCEL_KEY)}
            </TextButton>
            <Button
              variant="primary"
              /*
                No spinner on the control: the frame puts it in the body instead, so this is only
                ever inert while busy.
              */
              disabled={!confirmChecked || isBusy}
              onClick={handleConfirmCombine}
            >
              {intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_SUBMIT_KEY)}
            </Button>
          </Flex>
        }
      >
        {isBusy ? (
          /*
            role=status so assistive tech announces this politely as a transient state rather than
            interrupting with it. Composed from an icon and a heading rather than the shared Spinner
            component, whose wrapper stacks the icon above default-weight text; the frame puts the
            label beside the icon at heading size.
          */
          <ModalLoadingBody
            data-testid="combine-review-modal-loading"
            role="status"
          >
            <Icon type="spinner" size="lg" aria-hidden="true" />
            <Heading renderAs="h2" size="medium">
              {intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_LOADING_KEY)}
            </Heading>
          </ModalLoadingBody>
        ) : (
          <div>
            <Text>{intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_BODY_KEY)}</Text>
            <Box paddingBlockStart="xsmall" paddingBlockEnd="xsmall">
              <ul style={{ paddingInlineStart: '20px', margin: 0 }}>
                <li>
                  <Text size="small">
                    {intl.getMessage(
                      COMBINE_ACCOUNTS_CONFIRM_BULLET_CAMPAIGNS_KEY,
                    )}
                  </Text>
                </li>
                <li>
                  <Text size="small">
                    {intl.getMessage(
                      COMBINE_ACCOUNTS_CONFIRM_BULLET_INVOICES_KEY,
                    )}
                  </Text>
                </li>
                <li>
                  <Text size="small">
                    {intl.getMessage(COMBINE_ACCOUNTS_CONFIRM_BULLET_USERS_KEY)}
                  </Text>
                </li>
                <li>
                  <Text size="small">
                    {intl.getMessage(
                      COMBINE_ACCOUNTS_CONFIRM_BULLET_FEATURES_KEY,
                    )}
                  </Text>
                </li>
                <li>
                  <Text size="small">
                    {intl.getMessage(
                      COMBINE_ACCOUNTS_CONFIRM_BULLET_MANAGE_KEY,
                    )}
                  </Text>
                </li>
              </ul>
            </Box>
            <Checkbox
              id="review-confirm-checkbox"
              /*
                The acknowledgement carries a click-through to the Amazon Ads
                Agreement, so the link is a placeholder inside the translated
                sentence rather than concatenated around it. Storm's Checkbox
                already types its label as ReactNode. Matches RequestAccessTab,
                which presents the same click-through, and reuses its link label
                rather than minting a second id for identical words.
              */
              label={intl.formatMessage(COMBINE_ACCOUNTS_CONFIRM_CHECKBOX_KEY, {
                agreement: <AmazonAdsAgreementLink />,
              })}
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
            />
          </div>
        )}
      </Modal>
    </Box>
  );
};

export default CombineAccountsReviewPage;
